//sample SharedFile component below
import React from "react";
import { action, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import { QRCodeSVG } from "qrcode.react";

import apiService from "@services/apiService";
import type { ArchiveResponseEntry, FileDetailResponseItem } from "../models";
import { fileService } from "@services/fileService";
import { uploadService } from "@services/uploadService";
import { ImageWrapper } from "@components/ImageWrapper";
import { Timestamp } from "@components/Timestamp";
import ThemeToggler from "@components/ThemeToggler";
import { socketService } from "@services/socketService";
import type { FileProgressData } from "@services/socketService";
import { ImgDownload } from "@components/Images/ImgDownload";
import { ImgCopy } from "@components/Images/ImgCopy";import { ImgHourglass } from '@components/Images/ImgHourglass';
import { ImgUp } from '@components/Images/ImgUp';import { ImgLock } from '@components/Images/ImgLock';
import PdfViewer from "@components/PdfViewer";
import { FileIcon } from "@components/FileIcon";
import { getPreviewableType } from "@root/utils/fileUtils";
import ReactMarkdown from 'react-markdown';

type SharedFileProps = {
  fileId?: string;
  customName?: string;
  directory?: string;
};

@observer
class SharedFile extends React.Component<SharedFileProps> {
  @observable private _fileDetails: FileDetailResponseItem | null = null;
  @observable private _fileId: string = '';
  @observable private _currentFolder = '';
  @observable private _fileList: ArchiveResponseEntry[] = [];
  @observable private _imagesList: string[] = [];
  @observable private _directoriesList: string[] = [];
  @observable private _copyLinkLabel = 'Copy Link';
  @observable private _notFound = false;
  @observable private _uploadSpeed: number = 0;
  private _notFoundImg: string;
  @observable private _uploadTimeLeft: number = 0;
  @observable private _pdfData: ArrayBuffer | null = null;
  private _progressSamples: { ts: number; readySize: number }[] = [];
  private _lastSpeedUpdateTs: number = 0;
  private _progressInterval?: number;

  constructor(props: SharedFileProps) {
    super(props);
    this._fileId = props.fileId ?? '';
    this._currentFolder = this.props.directory ? this.props.directory + '/' : '';
    const notFoundImgs = ['/cats/hungry.png', '/cats/facepalm.apng', '/cats/cage.png'];
    this._notFoundImg = notFoundImgs[Math.floor(Math.random() * notFoundImgs.length)];
    makeObservable(this);
  }

  async componentDidMount() {
    await this._loadData();
    document.getElementsByClassName('ui-loader-container')[0]?.remove();
    if (this._fileDetails && !this._fileDetails.isComplete) {
      this._startProgressWatch();
    }
  }

  componentWillUnmount(): void {
    this._stopProgressWatch();
  }

  componentDidUpdate(prevProps: Readonly<SharedFileProps>): void {
    if (prevProps.fileId !== this.props.fileId || prevProps.customName !== this.props.customName || prevProps.directory !== this.props.directory) {
      runInAction(() => {
        this._fileId = this.props.fileId ?? '';
        this._currentFolder = this.props.directory ? this.props.directory + '/' : '';
        this._fileDetails = null;
        this._notFound = false;
        this._directoriesList = [];
      });
      this._loadData();
    }
  }

  render() {
    if (this._notFound) {
      return (
        <div id="sharePage" className="share-container">
          <a href="/" className="share-home-link" title="Go to home"><ImgLock size={16} /> FileVault</a>
          <div className="share-box" style={{ textAlign: 'center' }}>
            <img src={this._notFoundImg} alt="Cat illustration" style={{ height: '120px', marginBottom: '16px' }} />
            <h1 style={{ fontSize: '24px', marginBottom: '8px' }}>File Not Found</h1>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>This file does not exist or has been deleted.</p>
            <a href="/" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-block' }}>Go Home</a>
          </div>
        </div>
      );
    }

    if (!this._fileDetails) {
      return (
        <div id="sharePage" className="share-container">
          Loading...
        </div>
      );
    }

    //show images gallery if contains more than 50% of images
    const files = this._fileDetails.zipEntries ?? [];

    return (
      <div id="sharePage" className="share-container">
        <a href="/" className="share-home-link" title="Go to home"><ImgLock size={16} /> FileVault</a>
        <div className="share-box">
          <ThemeToggler style={{ position: 'absolute', top: '10px', right: '10px' }}/>
          {(() => {
            const strict = this._fileDetails.shareSettings?.viewMode === 'strict';
            const showQr = this._fileDetails.shareSettings?.showQrCode !== false;
            if (strict) {
              return (
                <div className="share-icon share-icon-strict">
                  <FileIcon fileName={this._fileDetails.fileName} />
                </div>
              );
            }
            return (
              <div className="share-icon" style={{ cursor: 'default' }}>
                {showQr
                  ? <QRCodeSVG value={window.location.href} level="M" size={150} marginSize={0} imageSettings={{
                      src: "/cats/cage.png",
                      height: 35,
                      width: 35,
                      excavate: true
                    }} />
                  : <img src="/cats/popcorn.apng" alt="File Share Illustration" />
                }
              </div>
            );
          })()}
          <h1 className="share-file-name">{this._fileDetails.fileName}</h1>
          <div className="share-file-info">
            {fileService.formatFileSize(this._fileDetails.fileSize)}
            {this._fileDetails.shareSettings?.viewMode === 'strict' && (
              <>
                {this._fileDetails.dateTime && (
                  <span style={{ marginLeft: '12px' }}>
                    Uploaded <Timestamp timestamp={this._fileDetails.dateTime} />
                  </span>
                )}
                {this._fileDetails.uploadedBy && (
                  <span style={{ marginLeft: '12px', opacity: 0.7 }}>by {this._fileDetails.uploadedBy}</span>
                )}
              </>
            )}
          </div>
          {this._fileDetails.shareSettings?.viewMode === 'strict' && this._fileDetails.shareSettings?.showQrCode !== false && (
            <div className="share-qr-strict">
              <QRCodeSVG value={window.location.href} level="M" size={120} marginSize={0} />
            </div>
          )}
          {!!this._fileDetails.unzipError && (
            <div className="share-archive-warning" role="status">
              Archive preview is unavailable for this file, but download still works.
            </div>
          )}
          {!this._fileDetails.isComplete && (
            <div className="share-upload-progress">
              <div className="share-upload-progress-bar">
                <div
                  className="share-upload-progress-fill"
                  style={{ width: `${this._fileDetails.fileSize > 0 ? Math.round(this._fileDetails.readySize / this._fileDetails.fileSize * 100) : 0}%` }}
                />
              </div>
              <div className="share-upload-progress-text">
                <ImgHourglass /> Uploading… {fileService.formatFileSize(this._fileDetails.readySize)} / {fileService.formatFileSize(this._fileDetails.fileSize)}
                {' '}({this._fileDetails.fileSize > 0 ? Math.round(this._fileDetails.readySize / this._fileDetails.fileSize * 100) : 0}%)
                {this._uploadSpeed > 0 && (
                  <span className="share-upload-speed">
                    {' · '}{uploadService.formatBitrate(this._uploadSpeed)}
                    {this._uploadTimeLeft > 0 && <>{' · '}{uploadService.formatTimeLeft(this._uploadTimeLeft)} left</>}
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="share-actions">
            <button
              className="btn btn-primary"
              onClick={this._onDownloadClick}
              disabled={!this._fileDetails.isComplete}
              title={this._fileDetails.isComplete ? 'Download file' : 'File is still uploading'}
            >
              {this._fileDetails.isComplete ? <><ImgDownload /> Download File</> : <><ImgHourglass /> Upload in progress…</>}
            </button>
            <button className="btn btn-secondary" onClick={this._onCopyLink} title="Copy share link">
              <ImgCopy /> {this._copyLinkLabel}
            </button>
          </div>
          {/* Direct links */}
          {(this._fileDetails.shareSettings?.showDirectLink || this._fileDetails.shareSettings?.showCdnLink) && (
            <div className="share-direct-links">
              {this._fileDetails.shareSettings?.showDirectLink && (
                <div className="share-direct-link-row">
                  <span className="share-direct-link-label">Direct download link:</span>
                  <div className="share-direct-link-field">
                    <input
                      className="share-direct-link-input"
                      type="text"
                      readOnly
                      value={`${window.location.origin}/api/file/${this._fileId}/download`}
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      className="btn btn-secondary share-direct-link-copy"
                      onClick={() => this._copyText(`${window.location.origin}/api/file/${this._fileId}/download`)}
                      title="Copy"
                    >
                      <ImgCopy />
                    </button>
                  </div>
                </div>
              )}
              {this._fileDetails.shareSettings?.showCdnLink && this._fileDetails.filePath && (
                <div className="share-direct-link-row">
                  <span className="share-direct-link-label">CDN link:</span>
                  <div className="share-direct-link-field">
                    {(() => {
                      const cdnUrl = `https://static-storage.illusdolphin.net${this._fileDetails!.filePath!.replace('/upload', '')}`;
                      return (
                        <>
                          <input
                            className="share-direct-link-input"
                            type="text"
                            readOnly
                            value={cdnUrl}
                            onFocus={(e) => e.target.select()}
                          />
                          <button
                            className="btn btn-secondary share-direct-link-copy"
                            onClick={() => this._copyText(cdnUrl)}
                            title="Copy"
                          >
                            <ImgCopy />
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Markdown description */}
        {this._fileDetails.shareSettings?.description && (
          <div className="share-description">
            <ReactMarkdown>{this._fileDetails.shareSettings.description}</ReactMarkdown>
          </div>
        )}
        {this._fileDetails.isComplete && (() => {
          const previewType = getPreviewableType(this._fileDetails.fileName);
          const isImg = this.isImageFile(this._fileDetails.fileName);
          if (!isImg && (!previewType || previewType === 'zip' || previewType === 'text')) return null;
          return (
            <div className="share-preview">
              {isImg && (
                <ImageWrapper
                  className="share-preview-image"
                  src={this._fileDetails.fileName}
                  alt={this._fileDetails.fileName}
                  onPreviewSrc={() => `/api/file/${this._fileId}/preview?width=900&height=900`}
                  onViewSrc={() => `/api/file/${this._fileId}/download`}
                  onDownloadSrc={() => `/api/file/${this._fileId}/download`}
                />
              )}
              {previewType === 'pdf' && (
                <PdfViewer pdfData={this._pdfData} />
              )}
              {previewType === 'video' && (
                <video
                  className="share-preview-video"
                  controls
                  src={`/api/file/${this._fileId}/download`}
                />
              )}
              {previewType === 'audio' && (
                <audio
                  className="share-preview-audio"
                  controls
                  src={`/api/file/${this._fileId}/download`}
                />
              )}
            </div>
          );
        })()}
        {files.length > 0 && this._fileDetails.shareSettings?.showZipContent !== false && (
          <div className="share-images-gallery" id="gallerySection">
            <h2>Files in this archive
              {/*<nav className="breadcrumb">
                <a href="/">Home</a>
                <span>›</span>
                <span>{this._fileDetails.fileName}</span>
                <span>›</span>
                <span id="currentPath">{this._currentFolder}</span>
                </nav>*/}
            </h2>

            <div className="file-grid">
              
              {this._fileList!.map(entry => {
                const isImage = this.isImageFile(entry.fileName);
                //get filename without path
                let fileName = entry.fileName;
                
                if (entry.isDirectory) {
                  //remove trailing slash for display
                  fileName = fileName.endsWith('/') ? fileName.slice(0, -1) : fileName;
                }

                fileName = fileName.split('/').pop() || entry.fileName;

                //up icon for self folder
                const isSelfFolder = this._currentFolder === entry.fileName;
                if (isSelfFolder){
                  fileName = "Go back";
                }

                let entrySize = entry.uncompressedSize;
                let directoryFilesCount = 0;
                if (entry.isDirectory) {                  
                  //calculate total size of directory
                  entrySize = 0;
                  for (const subEntry of this._fileDetails?.zipEntries || []) {
                    if (this._isFileInDirectory(subEntry, entry.fileName, true) && !subEntry.isDirectory) {
                      entrySize += subEntry.uncompressedSize;
                      directoryFilesCount++;
                    }
                  }
                }

                return (
                  <div className="file-card" onClick={() => {
                    if (entry.isDirectory) {
                      let newFolder =  entry.fileName;
                      if (isSelfFolder) {
                        //go up one level
                        const parts = this._currentFolder.split('/').filter(part => part.length > 0);
                        parts.pop();
                        newFolder = parts.length > 0 ? parts.join('/') + '/' : '';
                      }
                      this._setDirectory(newFolder);
                    }
                  }}>
                    <div className="file-preview">
                      {isImage && <ImageWrapper 
                        className="gallery-image"
                        key={entry.fileName}
                        src={entry.fileName}
                        alt={fileName}
                        fileList={this._imagesList}
                        onPreviewSrc={this._onGetPreviewImage}
                        onViewSrc={this._onGetViewImage}
                        onDownloadSrc={this._onGetDownloadImage}
                        onAltText={this._onAltText}
                      />}
                      {entry.isDirectory && isSelfFolder && <span><ImgUp /></span>}
                      {entry.isDirectory && !isSelfFolder && this._renderFolder(entry)}
                    </div>
                    <div className="file-name">{fileName}</div>
                    <div className="file-meta">
                        {!isSelfFolder && <span>{fileService.formatFileSize(entrySize)}</span>}
                        {!isSelfFolder && entry.lastModified && <Timestamp timestamp={entry.lastModified} />}
                    </div>
                </div>
                );
              })}
            </div>

          </div>
        )}
      </div>
    );
  }

  private _onGetPreviewImage = (entry: string): string => {
    return `/api/file/${this._fileId}/entry/${encodeURI(entry)}/preview?width=300&height=300`;
  }

  private _onGetViewImage = (entry: string): string => {
    return `/api/file/${this._fileId}/entry/${encodeURI(entry)}/preview?width=1536&height=1536`;
  }

  private _onGetDownloadImage = (entry: string): string => {
    return `/api/file/${this._fileId}/entry/${encodeURI(entry)}`;
  }

  private _onAltText = (entry: string): string => {
    return `Image preview for ${entry}`;
  }

  private isImageFile(fileName: string): boolean {
    const ext = fileService.getExtension(fileName).toLowerCase();
    return ext === 'jpg' || ext === 'jpeg' || ext === 'png';
  }

  private _renderFolder(entry: ArchiveResponseEntry) {
    const files = this._fileDetails?.zipEntries;
    const imagesInFolder = files?.filter(img => this.isImageFile(img.fileName) && this._isFileInDirectory(img, entry.fileName, true));
    
    const takeUpTo4ImagesInFolder = imagesInFolder?.filter(img => this._isFileInDirectory(img, entry.fileName, false)).slice(0, 4);
    return (<div className="folder-container">
      <div className="folder-tab" />
      <div className="folder-back">
        {imagesInFolder?.length && <div className="image-count">{imagesInFolder?.length}</div>}
        {takeUpTo4ImagesInFolder && <div className="folder-preview">
          {takeUpTo4ImagesInFolder.map((img, index) => (
            <img key={index} src={this._onGetPreviewImage(img.fileName)} alt={this._onAltText(img.fileName)} className="preview-image" />
          ))}
        </div>}
      </div>
    </div>);
  }

  private async _loadData() {
    let fileId = this.props.fileId ?? '';
    if (!fileId && this.props.customName) {
      const resolved = await apiService.get<{ id: string; fileName: string; isComplete: boolean }>(
        `/api/file/customName/${this.props.customName}`, { suppressStatuses: [404] }
      );
      runInAction(() => { this._fileId = resolved.id; });
      fileId = resolved.id;
    }
    if (!fileId) {
      runInAction(() => { this._notFound = true; });
      return;
    }
    try {
      const fileDetails = await apiService.get<FileDetailResponseItem>(`/api/file/${fileId}`, { suppressStatuses: [404] });
      if (fileDetails.isComplete && getPreviewableType(fileDetails.fileName) === 'pdf') {
        fetch(`/api/file/${fileId}/download`)
          .then(r => r.arrayBuffer())
          .then(buf => runInAction(() => { this._pdfData = buf; }))
          .catch(() => {});
      }
      runInAction(() => {
        this._fileDetails = fileDetails;
        let imagesCount = 0;
        if (fileDetails.zipEntries){
          for(const entry of fileDetails.zipEntries) {
            if (this.isImageFile(entry.fileName)) {
              imagesCount++;
            } else if (entry.isDirectory) {
              this._directoriesList.push(entry.fileName);
            }
          }
        }
        this._setDirectory(this._currentFolder);
      });
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 404) {
        runInAction(() => { this._notFound = true; });
      }
    }
  }

  private _isFileInDirectory(entry: ArchiveResponseEntry, directory: string, includeRecursive: boolean): boolean {

    //entry.fileName should start with current folder but not be nested deeper. Allow direct children only
    if (!entry.fileName.startsWith(directory)) {
      return false;
    }

    if (includeRecursive) {
      return true;
    }

    const relativePath = entry.fileName.substring(directory.length);
    if (relativePath.includes('/') && !entry.isDirectory) {
      //nested deeper, skip
      return false;
    }

    if (entry.isDirectory && relativePath.substring(0, relativePath.length - 1).includes('/')) {
      //nested deeper, skip
      return false;
    }

    return true;
  }

  private _setDirectory(directory: string) {
    this._currentFolder = directory;
    // update query string 'directory' parameter without reloading the page
    const url = new URL(window.location.href);
    if (directory) {
      url.searchParams.set('directory', directory.slice(0, -1)); //remove trailing slash
    } else {
      url.searchParams.delete('directory');
    }

    //add history state
    window.history.pushState({}, '', url.toString());    

    //scroll to gallery section
    const gallerySection = document.getElementById('gallerySection');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth' });
    }

    const fileList: ArchiveResponseEntry[] = [];
    const imagesList: string[] = [];
    for(const entry of this._fileDetails?.zipEntries || []) {
      
      //entry.fileName should start with current folder but not be nested deeper. Allow direct children only
      if (!this._isFileInDirectory(entry, directory, false)) {
        continue;
      }

      if (this.isImageFile(entry.fileName)) {
        imagesList.push(entry.fileName);
      }

      fileList.push(entry);
    }
    this._fileList = fileList;
    this._imagesList = imagesList;
  }

  @action.bound
  private _onDownloadClick() {
    if (!this._fileDetails || !this._fileDetails.isComplete) return;

    const data = this._fileDetails;
    var fileUrl = "https://static-storage.illusdolphin.net" + data.filePath.replace("/upload", "");
    window.location.href = fileUrl;
  }

  @action.bound
  private _onCopyLink() {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        runInAction(() => { this._copyLinkLabel = 'Copied!'; });
        setTimeout(() => runInAction(() => { this._copyLinkLabel = 'Copy Link'; }), 2000);
      }).catch(() => this._fallbackCopy(url));
    } else {
      this._fallbackCopy(url);
    }
  }

  private _fallbackCopy(url: string) {
    const input = document.createElement('input');
    input.value = url;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    runInAction(() => { this._copyLinkLabel = 'Copied!'; });
    setTimeout(() => runInAction(() => { this._copyLinkLabel = 'Copy Link'; }), 2000);
  }

  private _copyText(text: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
  }

  private _startProgressWatch() {
    socketService.connect();
    socketService.watchFile(this._fileId);
    socketService.on('file-progress', this._onFileProgress);
    this._progressInterval = window.setInterval(() => this._pollProgress(), 4000);
  }

  private _stopProgressWatch() {
    if (this._progressInterval) {
      window.clearInterval(this._progressInterval);
      this._progressInterval = undefined;
    }
    socketService.off('file-progress', this._onFileProgress);
    this._progressSamples = [];
    runInAction(() => {
      this._uploadSpeed = 0;
      this._uploadTimeLeft = 0;
    });
  }

  private _updateSpeedEstimate(readySize: number) {
    const now = Date.now();
    this._progressSamples.push({ ts: now, readySize });
    // Keep only samples from the last 10 seconds
    const cutoff = now - 10_000;
    this._progressSamples = this._progressSamples.filter(s => s.ts >= cutoff);
    if (now - this._lastSpeedUpdateTs < 1000) return;
    this._lastSpeedUpdateTs = now;
    if (this._progressSamples.length >= 2) {
      const oldest = this._progressSamples[0];
      const newest = this._progressSamples[this._progressSamples.length - 1];
      const elapsed = (newest.ts - oldest.ts) / 1000;
      const bytesPerSec = elapsed > 0 ? (newest.readySize - oldest.readySize) / elapsed : 0;
      const remaining = (this._fileDetails?.fileSize ?? 0) - readySize;
      runInAction(() => {
        this._uploadSpeed = bytesPerSec;
        this._uploadTimeLeft = bytesPerSec > 0 ? remaining / bytesPerSec : 0;
      });
    }
  }

  private _onFileProgress = (data: FileProgressData) => {
    if (data.file._id !== this._fileId) return;
    this._updateSpeedEstimate(data.file.readySize);
    runInAction(() => {
      if (this._fileDetails) {
        this._fileDetails.readySize = data.file.readySize;
        if (data.file.isComplete) {
          this._fileDetails.isComplete = true;
          if (data.file.filePath) {
            this._fileDetails.filePath = data.file.filePath;
          }
          this._stopProgressWatch();
          // Fully reload so the completed view matches a fresh page load
          // (zip entries, previews, share settings, etc. are fetched again).
          void this._loadData();
        }
      }
    });
  };

  private async _pollProgress() {
    if (!this._fileDetails || this._fileDetails.isComplete) {
      this._stopProgressWatch();
      return;
    }
    try {
      const details = await apiService.get<FileDetailResponseItem>(`/api/file/${this._fileId}`);
      this._updateSpeedEstimate(details.readySize);
      runInAction(() => {
        if (this._fileDetails) {
          this._fileDetails.readySize = details.readySize;
          if (details.isComplete) {
            this._fileDetails.isComplete = true;
            this._fileDetails.filePath = details.filePath;
            this._stopProgressWatch();
            // Fully reload so the completed view matches a fresh page load.
            void this._loadData();
          }
        }
      });
    } catch (error) {
      // ignore poll errors
      console.warn('Failed to poll file progress', error);
    }
  }
};

export default SharedFile;